/**
 * Type-safe API client factory
 *
 * Provides a configured fetch wrapper with consistent error handling,
 * JSON parsing, and authentication credential forwarding.
 */

import type {
  ActiveMedicationsResponse,
  ApiErrorResponse,
  ApiResponse,
  AudioUploadResponse,
  ConsentHistoryResponse,
  ConsentsResponse,
  CreateEventRequest,
  CreateLocationRequest,
  CreateMedicationRequest,
  CreatePainEntryRequest,
  CreateDoctorVisitRequest,
  CreateDoctorVisitItemRequest,
  DailyRecordResponse,
  DashboardResponse,
  DismissedInsightsResponse,
  DismissInsightRequest,
  DismissInsightResponse,
  DoctorVisitResponse,
  DoctorVisitItemResponse,
  EventCorrelationResponse,
  EventResponse,
  ExportDataResponse,
  ExportDoctorVisitRequest,
  ExtractRequest,
  ExtractionResult,
  GenerateExportRequest,
  GetInsightsParams,
  InsightsResponse,
  ListDailyRecordsResponse,
  ListDoctorVisitsResponse,
  ListEventsResponse,
  ListLocationsResponse,
  ListMedicationsResponse,
  ListWeatherResponse,
  LocationResponse,
  MedicationSuggestionsResponse,
  PainLocationResponse,
  SearchLocationsResponse,
  SuggestedTimeframeResponse,
  SwitchMedicationRequest,
  SwitchMedicationResponse,
  TranscriptionResponse,
  UpdateConsentsRequest,
  UpdateDoctorVisitRequest,
  UpdateDoctorVisitItemRequest,
  UpdateLocationRequest,
  UpdateMedicationRequest,
  UserMedicationResponse,
  UserProfile,
  UserSettings,
  UpdateUserProfileRequest,
  UpdateUserSettingsRequest,
  UpdateEventRequest,
  UpdatePainLocationRequest,
  VisitContextResponse,
} from "./types";
import { isApiError } from "./types";
import { ENDPOINTS, EXPORTS, EXTRACTION, INSIGHTS, LOCATIONS, MEDICATIONS, TRANSCRIPTIONS, WEATHER, DOCTOR_VISITS, CONSENTS } from "./endpoints";

/**
 * Client configuration options
 */
export interface ClientOptions {
  /** Custom headers to include with every request */
  headers?: Record<string, string>;
  /** Include credentials (cookies) with requests - defaults to true */
  credentials?: RequestCredentials;
  /** Request timeout in milliseconds */
  timeout?: number;
}

/**
 * Request options for individual API calls
 */
export interface RequestOptions {
  /** Additional headers for this request */
  headers?: Record<string, string>;
  /** AbortController signal for cancellation */
  signal?: AbortSignal;
}

/**
 * API client interface with typed HTTP methods
 */
export interface ApiClient {
  /** Perform a GET request */
  get<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>>;
  /** Perform a POST request with JSON body */
  post<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>>;
  /** Perform a PUT request with JSON body */
  put<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>>;
  /** Perform a DELETE request */
  delete<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>>;
  /** Create a new pain entry */
  createPainEntry(
    data: CreatePainEntryRequest
  ): Promise<ApiResponse<DailyRecordResponse>>;
  /** Fetch a single daily record by date */
  getDailyRecord(date: string): Promise<ApiResponse<DailyRecordResponse>>;
  /** List daily records with pagination */
  listDailyRecords(options?: {
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ListDailyRecordsResponse>;
  /** Update an existing pain location */
  updatePainLocation(
    id: string,
    data: UpdatePainLocationRequest
  ): Promise<ApiResponse<PainLocationResponse>>;
  /** Delete a pain location */
  deletePainLocation(id: string): Promise<void>;
  /** Get dashboard data */
  getDashboard(): Promise<ApiResponse<DashboardResponse>>;
  /** Get user settings */
  getUserSettings(): Promise<ApiResponse<UserSettings>>;
  /** Update user settings */
  updateUserSettings(
    data: UpdateUserSettingsRequest
  ): Promise<ApiResponse<UserSettings>>;
  /** Update user profile */
  updateUserProfile(
    data: UpdateUserProfileRequest
  ): Promise<ApiResponse<UserProfile>>;
  /** Get user profile */
  getUserProfile(): Promise<ApiResponse<UserProfile>>;
  /** Create a new event */
  createEvent(data: CreateEventRequest): Promise<ApiResponse<EventResponse>>;
  /** Get a single event by ID */
  getEvent(id: string): Promise<ApiResponse<EventResponse>>;
  /** List events with pagination and filters */
  listEvents(options?: {
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
    eventType?: string;
    relatedMedicationId?: string;
    q?: string;
  }): Promise<ListEventsResponse>;
  /** Update an existing event */
  updateEvent(
    id: string,
    data: UpdateEventRequest
  ): Promise<ApiResponse<EventResponse>>;
  /** Delete an event (soft delete) */
  deleteEvent(id: string): Promise<void>;
  /** Get event correlation analysis */
  getEventCorrelation(id: string): Promise<ApiResponse<EventCorrelationResponse>>;
  /** Upload audio file to blob storage */
  uploadAudio(
    audioBlob: Blob,
    filename?: string
  ): Promise<AudioUploadResponse>;
  /** Transcribe audio to text using Azure OpenAI */
  transcribeAudio(
    audioBlob: Blob,
    options?: { language?: string }
  ): Promise<TranscriptionResponse>;
  /** Extract pain data from transcript using AI */
  extractFromTranscript(
    request: ExtractRequest
  ): Promise<ExtractionResult>;
  /** Get AI-powered insights from pain data */
  getInsights(params?: GetInsightsParams): Promise<InsightsResponse>;
  /** Generate export data for a date range */
  generateExport(request: GenerateExportRequest): Promise<ExportDataResponse>;
  /** Get list of dismissed insight IDs */
  listDismissedInsights(): Promise<DismissedInsightsResponse>;
  /** Dismiss an insight */
  dismissInsight(insightId: string): Promise<DismissInsightResponse>;
  /** Restore a dismissed insight (undismiss) */
  restoreDismissedInsight(insightId: string): Promise<void>;
  /** Clear all dismissed insights */
  clearDismissedInsights(): Promise<void>;
  /** Get medication name suggestions from user history */
  getMedicationSuggestions(query?: string): Promise<MedicationSuggestionsResponse>;
  /** Get active medications for a given date (defaults to today) */
  getActiveMedications(options?: { date?: string }): Promise<ActiveMedicationsResponse>;
  /** List user medications with status filter and pagination */
  listMedications(options?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListMedicationsResponse>;
  /** Create a new user medication */
  createMedication(data: CreateMedicationRequest): Promise<ApiResponse<UserMedicationResponse>>;
  /** Update an existing user medication */
  updateMedication(id: string, data: UpdateMedicationRequest): Promise<ApiResponse<UserMedicationResponse>>;
  /** Switch from one medication to another */
  switchMedication(id: string, data: SwitchMedicationRequest): Promise<ApiResponse<SwitchMedicationResponse>>;
  /** Soft-delete an inactive user medication (sets deletedAt) */
  deleteMedication(id: string): Promise<void>;
  /** List the user's geographic locations */
  listLocations(): Promise<ListLocationsResponse>;
  /** Search valid city locations */
  searchLocations(query: string): Promise<SearchLocationsResponse>;
  /** Create a location by geocoding a city query */
  createLocation(data: CreateLocationRequest): Promise<ApiResponse<LocationResponse>>;
  /** Update a location (rename and/or set as default) */
  updateLocation(id: string, data: UpdateLocationRequest): Promise<ApiResponse<LocationResponse>>;
  /** Delete a location (soft-delete) */
  deleteLocation(id: string): Promise<void>;
  /** List weather daily data for a date range */
  listWeatherDaily(options: {
    startDate: string;
    endDate: string;
  }): Promise<ListWeatherResponse>;
  /** List doctor visits with optional filters and pagination */
  listDoctorVisits(options?: {
    status?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListDoctorVisitsResponse>;
  /** Get a single doctor visit by ID */
  getDoctorVisit(id: string): Promise<ApiResponse<DoctorVisitResponse>>;
  /** Create a new doctor visit */
  createDoctorVisit(data: CreateDoctorVisitRequest): Promise<ApiResponse<DoctorVisitResponse>>;
  /** Update an existing doctor visit */
  updateDoctorVisit(id: string, data: UpdateDoctorVisitRequest): Promise<ApiResponse<DoctorVisitResponse>>;
  /** List items (talking points) for a doctor visit */
  listDoctorVisitItems(visitId: string): Promise<ApiResponse<DoctorVisitItemResponse[]>>;
  /** Create one or more items for a doctor visit */
  createDoctorVisitItem(visitId: string, data: CreateDoctorVisitItemRequest): Promise<ApiResponse<DoctorVisitItemResponse[]>>;
  /** Update a single visit item */
  updateDoctorVisitItem(visitId: string, itemId: string, data: UpdateDoctorVisitItemRequest): Promise<ApiResponse<DoctorVisitItemResponse>>;
  /** Get suggested timeframe for visit prep */
  getSuggestedTimeframe(options?: {
    visitType?: string;
    clinician?: string;
    visitId?: string;
  }): Promise<ApiResponse<SuggestedTimeframeResponse>>;
  /** Get visit prep context (aggregated data for a date range) */
  getVisitContext(visitId: string, options?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<VisitContextResponse>>;
  /** Export visit packet (returns blob for PDF, text for text format) */
  exportDoctorVisit(visitId: string, data: ExportDoctorVisitRequest): Promise<Response>;
  /** Get current consent state and policy version */
  getConsents(): Promise<ApiResponse<ConsentsResponse>>;
  /** Update consent decisions */
  updateConsents(data: UpdateConsentsRequest): Promise<ApiResponse<ConsentsResponse>>;
  /** Get consent audit history */
  getConsentHistory(): Promise<ApiResponse<ConsentHistoryResponse>>;
}

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = "ApiClientError";
  }

  static fromResponse(response: ApiErrorResponse): ApiClientError {
    return new ApiClientError(
      response.error,
      response.status,
      response.code,
      response.details
    );
  }
}

/**
 * Parse response body as JSON, returning null for empty/no-content bodies.
 *
 * Previously returned `{} as T` which silently masked missing data.
 * Now returns `null` so callers can distinguish "no body" from "has body".
 */
async function parseResponse<T>(response: Response): Promise<T | null> {
  const text = await response.text();
  if (!text) {
    return null;
  }
  return JSON.parse(text) as T;
}

/**
 * Handle fetch response, throwing on errors.
 *
 * For successful responses with no body (e.g. 204 No Content), returns
 * `{ data: undefined }` — appropriate for void endpoints.
 */
async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  const body = await parseResponse<ApiResponse<T> | ApiErrorResponse>(response);

  if (!response.ok) {
    if (body !== null && isApiError(body)) {
      const errorBody = body as ApiErrorResponse;
      throw new ApiClientError(
        errorBody.error || response.statusText,
        errorBody.status || response.status,
        errorBody.code || "UNKNOWN_ERROR",
        errorBody.details
      );
    }
    // No body or non-error-shaped body on a failed response
    throw new ApiClientError(
      response.statusText || `Request failed with status ${response.status}`,
      response.status,
      "UNKNOWN_ERROR"
    );
  }

  // Successful response with body that looks like an API error (shouldn't
  // happen in practice, but keep the guard for safety)
  if (body !== null && isApiError(body)) {
    const errorBody = body as ApiErrorResponse;
    throw new ApiClientError(
      errorBody.error || response.statusText,
      errorBody.status || response.status,
      errorBody.code || "UNKNOWN_ERROR",
      errorBody.details
    );
  }

  // Successful response with no body (e.g. 204 No Content)
  if (body === null) {
    return { data: undefined as unknown as T };
  }

  return body as ApiResponse<T>;
}

/**
 * Throw an ApiClientError for non-ok responses or error-shaped bodies.
 *
 * Shared by inline methods that call `parseResponse` directly
 * (instead of routing through `handleResponse`).
 */
function throwIfError(response: Response, body: unknown): void {
  if (!response.ok) {
    if (body !== null && isApiError(body)) {
      const errorBody = body as ApiErrorResponse;
      throw new ApiClientError(
        errorBody.error || response.statusText,
        errorBody.status || response.status,
        errorBody.code || "UNKNOWN_ERROR",
        errorBody.details
      );
    }
    throw new ApiClientError(
      response.statusText || `Request failed with status ${response.status}`,
      response.status,
      "UNKNOWN_ERROR"
    );
  }
  if (body !== null && isApiError(body)) {
    const errorBody = body as ApiErrorResponse;
    throw new ApiClientError(
      errorBody.error || response.statusText,
      errorBody.status || response.status,
      errorBody.code || "UNKNOWN_ERROR",
      errorBody.details
    );
  }
}

/**
 * Create a type-safe API client
 *
 * @param baseUrl - Base URL for all API requests (e.g., 'http://localhost:3000')
 * @param options - Client configuration options
 * @returns Configured API client instance
 *
 * @example
 * ```typescript
 * const api = createApiClient('http://localhost:3000');
 *
 * // GET request
 * const session = await api.get<Session>('/api/auth/get-session');
 *
 * // POST request with body
 * const entry = await api.post<PainEntry>('/api/entries', { intensity: 5 });
 * ```
 */
export function createApiClient(
  baseUrl: string,
  options: ClientOptions = {}
): ApiClient {
  const {
    headers: defaultHeaders = {},
    credentials = "include",
    timeout = 30000,
  } = options;

  const buildUrl = (path: string): string => {
    const normalizedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${normalizedBase}${normalizedPath}`;
  };

  const buildHeaders = (
    requestHeaders?: Record<string, string>
  ): Record<string, string> => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    ...defaultHeaders,
    ...requestHeaders,
  });

  const buildQueryString = (params: Record<string, string | number | undefined>): string => {
    const filtered = Object.entries(params)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    return filtered.length > 0 ? `?${filtered.join("&")}` : "";
  };

  /**
   * Create a timeout signal with cleanup function.
   * Returns both the AbortSignal and a cleanup function that MUST be called
   * when the request completes (success, error, or abort) to prevent timer leaks.
   */
  const createTimeoutSignal = (
    existingSignal?: AbortSignal
  ): { signal: AbortSignal | undefined; cleanup: () => void } => {
    if (typeof AbortController === "undefined") {
      return { signal: existingSignal, cleanup: () => {} };
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Forward abort from an existing signal (e.g. user-provided cancellation)
    if (existingSignal) {
      existingSignal.addEventListener(
        "abort",
        () => {
          clearTimeout(timeoutId);
          controller.abort();
        },
        { once: true }
      );
    }

    const cleanup = () => {
      clearTimeout(timeoutId);
    };

    return { signal: controller.signal, cleanup };
  };

  const request = async <T>(
    method: string,
    path: string,
    body?: unknown,
    requestOptions?: RequestOptions
  ): Promise<ApiResponse<T>> => {
    const url = buildUrl(path);
    const headers = buildHeaders(requestOptions?.headers);
    const { signal, cleanup } = createTimeoutSignal(requestOptions?.signal);

    try {
      const response = await fetch(url, {
        method,
        headers,
        credentials,
        signal,
        ...(body !== undefined && { body: JSON.stringify(body) }),
      });

      return handleResponse<T>(response);
    } finally {
      cleanup();
    }
  };

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>("GET", path, undefined, options),

    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>("POST", path, body, options),

    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>("PUT", path, body, options),

    delete: <T>(path: string, options?: RequestOptions) =>
      request<T>("DELETE", path, undefined, options),

    createPainEntry: (data: CreatePainEntryRequest) =>
      request<DailyRecordResponse>("POST", ENDPOINTS.painEntries, data),

    getDailyRecord: (date: string) =>
      request<DailyRecordResponse>("GET", ENDPOINTS.DAILY_RECORD_BY_DATE(date)),

    listDailyRecords: async (options = {}) => {
      const { limit, offset, startDate, endDate } = options;
      const queryString = buildQueryString({
        limit,
        offset,
        startDate,
        endDate,
      });
      const url = buildUrl(`${ENDPOINTS.DAILY_RECORDS}${queryString}`);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        // Parse response directly as ListDailyRecordsResponse (not wrapped in ApiResponse)
        const body = await parseResponse<ListDailyRecordsResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as ListDailyRecordsResponse;
      } finally {
        cleanup();
      }
    },

    updatePainLocation: (id: string, data: UpdatePainLocationRequest) =>
      request<PainLocationResponse>("PUT", `${ENDPOINTS.PAIN_LOCATION}/${id}`, data),

    deletePainLocation: async (id: string) => {
      await request<void>("DELETE", `${ENDPOINTS.PAIN_LOCATION}/${id}`);
    },

    getDashboard: () =>
      request<DashboardResponse>("GET", ENDPOINTS.DASHBOARD),

    getUserSettings: () =>
      request<UserSettings>("GET", ENDPOINTS.USER_SETTINGS),

    updateUserSettings: (data: UpdateUserSettingsRequest) =>
      request<UserSettings>("PUT", ENDPOINTS.USER_SETTINGS, data),

    updateUserProfile: (data: UpdateUserProfileRequest) =>
      request<UserProfile>("PUT", ENDPOINTS.USER_PROFILE, data),

    getUserProfile: () =>
      request<UserProfile>("GET", ENDPOINTS.USER_PROFILE),

    createEvent: (data: CreateEventRequest) =>
      request<EventResponse>("POST", ENDPOINTS.EVENTS, data),

    getEvent: (id: string) =>
      request<EventResponse>("GET", ENDPOINTS.EVENT_BY_ID(id)),

    listEvents: async (options = {}) => {
      const { limit, offset, startDate, endDate, eventType, relatedMedicationId, q } = options;
      const queryString = buildQueryString({
        limit,
        offset,
        startDate,
        endDate,
        eventType,
        relatedMedicationId,
        q,
      });
      const url = buildUrl(`${ENDPOINTS.EVENTS}${queryString}`);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        const body = await parseResponse<ListEventsResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as ListEventsResponse;
      } finally {
        cleanup();
      }
    },

    updateEvent: (id: string, data: UpdateEventRequest) =>
      request<EventResponse>("PATCH", ENDPOINTS.EVENT_BY_ID(id), data),

    deleteEvent: async (id: string) => {
      await request<void>("DELETE", ENDPOINTS.EVENT_BY_ID(id));
    },

    getEventCorrelation: (id: string) =>
      request<EventCorrelationResponse>("GET", ENDPOINTS.EVENT_CORRELATION(id)),

    uploadAudio: async (audioBlob: Blob, filename?: string) => {
      const formData = new FormData();
      formData.append("audio", audioBlob, filename ?? "recording.webm");
      if (filename) {
        formData.append("filename", filename);
      }

      const url = buildUrl(ENDPOINTS.AUDIO_UPLOADS);
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "POST",
          credentials,
          signal,
          body: formData,
          // Don't set Content-Type - browser will set it with boundary
        });

        const body = await parseResponse<AudioUploadResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as AudioUploadResponse;
      } finally {
        cleanup();
      }
    },

    transcribeAudio: async (
      audioBlob: Blob,
      options?: { language?: string }
    ) => {
      const formData = new FormData();
      formData.append("audio", audioBlob);
      if (options?.language) {
        formData.append("language", options.language);
      }

      const url = buildUrl(TRANSCRIPTIONS.CREATE);
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "POST",
          credentials,
          signal,
          body: formData,
          // Don't set Content-Type - browser will set it with boundary
        });

        const body = await parseResponse<TranscriptionResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as TranscriptionResponse;
      } finally {
        cleanup();
      }
    },

    extractFromTranscript: async (extractRequest: ExtractRequest) => {
      const url = buildUrl(EXTRACTION.EXTRACT);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "POST",
          headers,
          credentials,
          signal,
          body: JSON.stringify(extractRequest),
        });

        const body = await parseResponse<ExtractionResult | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as ExtractionResult;
      } finally {
        cleanup();
      }
    },

    getInsights: async (params?: GetInsightsParams) => {
      const queryString = buildQueryString({
        dateRange: params?.dateRange,
        lang: params?.language,
      });
      const url = buildUrl(`${INSIGHTS.GET}${queryString}`);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        const body = await parseResponse<InsightsResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as InsightsResponse;
      } finally {
        cleanup();
      }
    },

    /**
     * Generate export data for a date range
     */
    generateExport: async (request: GenerateExportRequest) => {
      const url = buildUrl(EXPORTS.GENERATE);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "POST",
          headers,
          credentials,
          signal,
          body: JSON.stringify(request),
        });

        const body = await parseResponse<ApiResponse<ExportDataResponse> | ApiErrorResponse>(response);

        throwIfError(response, body);

        const result = body as ApiResponse<ExportDataResponse>;
        return result.data;
      } finally {
        cleanup();
      }
    },

    /**
     * Get list of dismissed insight IDs
     */
    listDismissedInsights: async () => {
      const url = buildUrl(INSIGHTS.LIST_DISMISSED);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        const body = await parseResponse<DismissedInsightsResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as DismissedInsightsResponse;
      } finally {
        cleanup();
      }
    },

    /**
     * Dismiss an insight
     */
    dismissInsight: async (insightId: string) => {
      const url = buildUrl(INSIGHTS.DISMISS);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "POST",
          headers,
          credentials,
          signal,
          body: JSON.stringify({ insightId } as DismissInsightRequest),
        });

        const body = await parseResponse<DismissInsightResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as DismissInsightResponse;
      } finally {
        cleanup();
      }
    },

    /**
     * Restore a dismissed insight (undismiss)
     */
    restoreDismissedInsight: async (insightId: string) => {
      const url = buildUrl(INSIGHTS.RESTORE_DISMISSED(insightId));
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "DELETE",
          headers,
          credentials,
          signal,
        });

        if (!response.ok) {
          const body = await parseResponse<ApiErrorResponse>(response);
          throwIfError(response, body);
        }
      } finally {
        cleanup();
      }
    },

    /**
     * Clear all dismissed insights
     */
    clearDismissedInsights: async () => {
      const url = buildUrl(INSIGHTS.CLEAR_DISMISSED);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "DELETE",
          headers,
          credentials,
          signal,
        });

        if (!response.ok) {
          const body = await parseResponse<ApiErrorResponse>(response);
          throwIfError(response, body);
        }
      } finally {
        cleanup();
      }
    },

    /**
     * Get medication name suggestions from user history
     */
    getMedicationSuggestions: async (query?: string) => {
      const queryString = buildQueryString({ q: query });
      const url = buildUrl(`${MEDICATIONS.SUGGESTIONS}${queryString}`);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        const body = await parseResponse<MedicationSuggestionsResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as MedicationSuggestionsResponse;
      } finally {
        cleanup();
      }
    },

    /**
     * Get active medications for a given date (defaults to today on server)
     */
    getActiveMedications: async (options = {}) => {
      const queryString = buildQueryString({ date: (options as { date?: string }).date });
      const url = buildUrl(`${ENDPOINTS.ACTIVE_MEDICATIONS}${queryString}`);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        const body = await parseResponse<ActiveMedicationsResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as ActiveMedicationsResponse;
      } finally {
        cleanup();
      }
    },

    /**
     * List user medications with status filter and pagination
     */
    listMedications: async (options = {}) => {
      const { status, limit, offset } = options;
      const queryString = buildQueryString({
        status,
        limit,
        offset,
      });
      const url = buildUrl(`${MEDICATIONS.LIST}${queryString}`);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        const body = await parseResponse<ListMedicationsResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as ListMedicationsResponse;
      } finally {
        cleanup();
      }
    },

    /**
     * Create a new user medication
     */
    createMedication: (data: CreateMedicationRequest) =>
      request<UserMedicationResponse>("POST", MEDICATIONS.CREATE, data),

    /**
     * Update an existing user medication
     */
    updateMedication: (id: string, data: UpdateMedicationRequest) =>
      request<UserMedicationResponse>("PATCH", MEDICATIONS.BY_ID(id), data),

    /**
     * Switch from one medication to another
     */
    switchMedication: (id: string, data: SwitchMedicationRequest) =>
      request<SwitchMedicationResponse>("POST", MEDICATIONS.SWITCH(id), data),

    /**
     * Soft-delete an inactive user medication (sets deletedAt)
     */
    deleteMedication: async (id: string): Promise<void> => {
      await request<void>("DELETE", MEDICATIONS.BY_ID(id));
    },

    /**
     * List the user's geographic locations
     */
    listLocations: async () => {
      const url = buildUrl(LOCATIONS.LIST);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        const body = await parseResponse<ListLocationsResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as ListLocationsResponse;
      } finally {
        cleanup();
      }
    },

    /**
     * Search valid cities for location input.
     */
    searchLocations: async (query: string) => {
      const queryString = buildQueryString({ q: query });
      const url = buildUrl(`${LOCATIONS.SEARCH}${queryString}`);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        const body = await parseResponse<SearchLocationsResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as SearchLocationsResponse;
      } finally {
        cleanup();
      }
    },

    /**
     * Create a location by geocoding a city query
     */
    createLocation: (data: CreateLocationRequest) =>
      request<LocationResponse>("POST", LOCATIONS.CREATE, data),

    /**
     * Update a location (rename and/or set as default)
     */
    updateLocation: (id: string, data: UpdateLocationRequest) =>
      request<LocationResponse>("PATCH", LOCATIONS.BY_ID(id), data),

    /**
     * Delete a location (soft-delete)
     */
    deleteLocation: async (id: string): Promise<void> => {
      await request<void>("DELETE", LOCATIONS.BY_ID(id));
    },

    /**
     * List weather daily data for a date range
     */
    listWeatherDaily: async (options: { startDate: string; endDate: string }) => {
      const { startDate, endDate } = options;
      const queryString = buildQueryString({ startDate, endDate });
      const url = buildUrl(`${WEATHER.GET}${queryString}`);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        const body = await parseResponse<ListWeatherResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as ListWeatherResponse;
      } finally {
        cleanup();
      }
    },

    /**
     * List doctor visits with optional filters and pagination
     */
    listDoctorVisits: async (options = {}) => {
      const { status, from, to, limit, offset } = options as {
        status?: string;
        from?: string;
        to?: string;
        limit?: number;
        offset?: number;
      };
      const queryString = buildQueryString({
        status,
        from,
        to,
        limit,
        offset,
      });
      const url = buildUrl(`${DOCTOR_VISITS.LIST}${queryString}`);
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers,
          credentials,
          signal,
        });

        const body = await parseResponse<ListDoctorVisitsResponse | ApiErrorResponse>(response);

        throwIfError(response, body);

        return body as ListDoctorVisitsResponse;
      } finally {
        cleanup();
      }
    },

    /**
     * Get a single doctor visit by ID
     */
    getDoctorVisit: (id: string) =>
      request<DoctorVisitResponse>("GET", DOCTOR_VISITS.BY_ID(id)),

    /**
     * Create a new doctor visit
     */
    createDoctorVisit: (data: CreateDoctorVisitRequest) =>
      request<DoctorVisitResponse>("POST", DOCTOR_VISITS.CREATE, data),

    /**
     * Update an existing doctor visit
     */
    updateDoctorVisit: (id: string, data: UpdateDoctorVisitRequest) =>
      request<DoctorVisitResponse>("PATCH", DOCTOR_VISITS.BY_ID(id), data),

    /**
     * List items (talking points) for a doctor visit
     */
    listDoctorVisitItems: (visitId: string) =>
      request<DoctorVisitItemResponse[]>("GET", DOCTOR_VISITS.ITEMS(visitId)),

    /**
     * Create one or more items for a doctor visit
     */
    createDoctorVisitItem: (visitId: string, data: CreateDoctorVisitItemRequest) =>
      request<DoctorVisitItemResponse[]>("POST", DOCTOR_VISITS.ITEMS(visitId), data),

    /**
     * Update a single visit item
     */
    updateDoctorVisitItem: (visitId: string, itemId: string, data: UpdateDoctorVisitItemRequest) =>
      request<DoctorVisitItemResponse>("PATCH", DOCTOR_VISITS.ITEM_BY_ID(visitId, itemId), data),

    /**
     * Get suggested timeframe for visit prep
     */
    getSuggestedTimeframe: (options = {}) => {
      const { visitType, clinician, visitId } = options as {
        visitType?: string;
        clinician?: string;
        visitId?: string;
      };
      const queryString = buildQueryString({
        visitType,
        clinician,
        visitId,
      });
      return request<SuggestedTimeframeResponse>("GET", `${DOCTOR_VISITS.SUGGESTED_TIMEFRAME}${queryString}`);
    },

    /**
     * Get visit prep context (aggregated data for a date range)
     */
    getVisitContext: (visitId: string, options = {}) => {
      const { startDate, endDate } = options as {
        startDate?: string;
        endDate?: string;
      };
      const queryString = buildQueryString({
        startDate,
        endDate,
      });
      return request<VisitContextResponse>("GET", `${DOCTOR_VISITS.CONTEXT(visitId)}${queryString}`);
    },

    /**
     * Export visit packet (returns raw Response for blob handling)
     */
    exportDoctorVisit: async (visitId: string, data: ExportDoctorVisitRequest): Promise<Response> => {
      const url = buildUrl(DOCTOR_VISITS.EXPORT(visitId));
      const headers = buildHeaders();
      const { signal, cleanup } = createTimeoutSignal();

      try {
        const response = await fetch(url, {
          method: "POST",
          headers,
          credentials,
          signal,
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          const body = await parseResponse<ApiErrorResponse>(response);
          throwIfError(response, body);
        }

        return response;
      } finally {
        cleanup();
      }
    },

    // === Consent Management (GDPR Art. 9/13) ===

    /**
     * Get current consent state for the authenticated user
     */
    getConsents: () => {
      return request<ConsentsResponse>("GET", CONSENTS.GET);
    },

    /**
     * Update consent decisions (give/withdraw per consent type)
     */
    updateConsents: (data: UpdateConsentsRequest) => {
      return request<ConsentsResponse>("PUT", CONSENTS.UPDATE, data);
    },

    /**
     * Get consent audit history for the authenticated user
     */
    getConsentHistory: () => {
      return request<ConsentHistoryResponse>("GET", CONSENTS.HISTORY);
    },
  };
}
